import { api } from '@/api/http'

export async function fetchLastUpdated() {
  const { data } = await api.get('/last-updated')
  return data.last_updated
}

export async function fetchOverview() {
  const { data } = await api.get('/overview')
  return data
}

export async function fetchDetail(countryCode) {
  const { data } = await api.get(`/detail/${countryCode}`)
  return data
}

export async function fetchDevices(countryCode, customerName) {
  const { data } = await api.get(
    `/devices/${countryCode}/${encodeURIComponent(customerName)}`,
  )
  return data
}

export async function fetchLocations(countryCode) {
  const { data } = await api.get(`/locations/${countryCode}`)
  return data
}

// 유량(flow) 일별 (MNF) 차트 데이터 — api/API21/flow/day/{pointId}?to=...&limit=...
//   압력(fetchPressDay) 과 동일한 시그니처 — point id 를 path 에, to + limit 을 params 에
export async function fetchFlowDay(pointId, toDate, limit = 1, signal) {
  const { data } = await api.get(`api/API21/flow/day/${pointId}`, {
    params: { to: toDate, limit },
    signal,
  })
  if (Array.isArray(data?.results?.[0])) return data.results[0]
  if (Array.isArray(data?.results))      return data.results
  return []
}

// 유량(flow) 분단위 (Raw) 차트 데이터 — api/API21/flow/minute/{pointId}?to=...&limit=...
export async function fetchFlowMinute(pointId, toDate, limit = 3, signal) {
  const { data } = await api.get(`api/API21/flow/minute/${pointId}`, {
    params: { to: toDate, limit },
    signal,
  })
  if (Array.isArray(data?.results?.[0])) return data.results[0]
  if (Array.isArray(data?.results))      return data.results
  return []
}

// 유량(flow) 모니터링 지점 리스트 — 레거시 대시보드 유량 탭의 데이터 소스
//   GET api/API21/flow/list?customer_id={id}&search={s}&sort={k}&type={t}&to={yyyy-mm-dd HH:MM:SS}
//   응답: { results: [ [ {...}, ... ] ] } — results[0] 가 실제 배열
//   특수 케이스: 호출측에서 customer_id === 27 인 경우 1 로 치환 (legacy 동작)
export async function fetchFlowList({ customerId, search = '', sort = '', type = '', to = '' } = {}, signal) {
  const { data } = await api.get('api/API21/flow/list', {
    params: { customer_id: customerId, search, sort, type, to },
    signal,
  })
  // results[0] 가 배열인 경우 우선, 아니면 results 자체, 그것도 아니면 빈 배열
  if (Array.isArray(data?.results?.[0])) return data.results[0]
  if (Array.isArray(data?.results))      return data.results
  return []
}

export async function fetchPressDay(pointId, toDate, limit = 1) {
  const { data } = await api.get(`api/API21/m2_press/day/${pointId}`, { params: { to: toDate, limit } })
  return data.results[0]
}

// AI Agent 전용 — 일자 범위(from/to)로 단일 지점의 일별(MNP) 데이터
//   GET api/API21/m2_press/day/{pointId}?to={yyyy-mm-dd}&from={yyyy-mm-dd}
export async function fetchPressDayRange(pointId, toDate, fromDate, signal) {
  const { data } = await api.get(`api/API21/m2_press/day/${pointId}`, {
    params: { to: toDate, from: fromDate },
    signal,
  })
  if (Array.isArray(data?.results?.[0])) return data.results[0]
  if (Array.isArray(data?.results))      return data.results
  return []
}

export async function fetchPressMinute(pointId, toDate, limit = 3) {
  const { data } = await api.get(`api/API21/m2_press/minute/${pointId}`, { params: { to: toDate, limit } })
  return data.results[0]
}

// ── 장비 상세(M2 Monitoring) 팝업용 API ──
//   레거시 DlgM2MoniSetting.vue 의 호출 패턴과 동일하게 구성
//   - fetch  : GET api/get_m2_monitoring_setting?id={id}
//   - 상세 update: GET api/update_customer_m2_point?id={id}&fields={URI(JSON)}
//   - 셋팅1 update: GET api/update_m2_point_device_setting?id={id}&cd_id={cd_id}&fields={URI(JSON)}
//   - 셋팅2 update: PUT api/m2_point_device_option/{id}/{cd_id}  body=params
//   - baseline   : POST api/set_judge_level  body={type, type_id, level}
//   - point_status 변경: GET api/del_place?id={id}&device_id={cd_id}&place_status={status}
//   - 삭제       : GET api/delete_ex?p_idx_ex={id}
//   - status 일별 : GET api/API21/m2_device_status/day/{id}?to={l_date}&limit={N}
//   - data/health: GET api/monitoring/status_list?type={type}&device_sn={sn}&start_dt={s}&end_dt={e}
export async function fetchPointDetail(pointId) {
  const { data } = await api.get('api/get_m2_monitoring_setting', { params: { id: pointId } })
  return data?.list?.[0] ?? data?.list ?? data
}

// 변경된 properties(상세) 만 fields 로 직렬화 — fields={URI(JSON.stringify(diff))}
export async function updatePointDetail(pointId, fields) {
  const json = encodeURIComponent(JSON.stringify(fields || {}))
  const { data } = await api.get('api/update_customer_m2_point', {
    params: { id: pointId, fields: json },
    paramsSerializer: { encode: v => v },
  })
  return data
}

// 셋팅1 — 압력/누수/유량 통신주기 등
export async function updatePointSetting1(pointId, cdId, fields) {
  const json = encodeURIComponent(JSON.stringify(fields || {}))
  const { data } = await api.get('api/update_m2_point_device_setting', {
    params: { id: pointId, cd_id: cdId, fields: json },
    paramsSerializer: { encode: v => v },
  })
  return data
}

// 셋팅2 — 수충격/STEPTEST 옵션
export async function updatePointSetting2(pointId, cdId, payload) {
  const { data } = await api.put(`api/m2_point_device_option/${pointId}/${cdId}`, payload)
  return data
}

// baseline (누수음 AI 분석 기준값)
export async function setJudgeLevel({ type = 'monitoring', typeId, level }) {
  const { data } = await api.post('api/set_judge_level', { type, type_id: typeId, level })
  return data
}

// 모니터링 baseline 조회 — 레거시 set_show_baseline()
//   GET api/get_judge_level?type=monitoring&type_id={id}
//   응답: { check, level } — level 이 null/undefined 이면 호출측에서 0.7 default
export async function fetchMonitoringJudgeLevel(typeId, signal) {
  const { data } = await api.get('api/get_judge_level', {
    params: { type: 'monitoring', type_id: typeId },
    signal,
  })
  return data
}

// 모니터링 baseline 재계산 — start_date/end_date 구간의 측정 데이터에 새 level 일괄 적용
//   레거시: GET api/set_judge_level_ai_data?type=monitoring&m2_point_id={id}&judge_level={lvl}&start_date={s}&end_date={e}
//   응답: { check, successLength, failLength } — check===1 이면 실패
export async function recalcMonitoringJudgeLevel({ m2PointId, judgeLevel, startDate, endDate }, signal) {
  const { data } = await api.get('api/set_judge_level_ai_data', {
    params: {
      type: 'monitoring',
      m2_point_id: m2PointId,
      judge_level: judgeLevel,
      start_date: startDate,
      end_date: endDate,
    },
    signal,
  })
  return data
}

// 배터리 교체일자 변경 — 셋팅 탭 battery_replacement_date 전용
//   POST api/update_m2_battery_replace_date  body={customer_id, m2_point_id, m2_device_id, replace_date}
export async function updateBatteryReplaceDate({ customerId, m2PointId, m2DeviceId, replaceDate }) {
  const { data } = await api.post('api/update_m2_battery_replace_date', {
    customer_id: customerId,
    m2_point_id: m2PointId,
    m2_device_id: m2DeviceId,
    replace_date: replaceDate,
  })
  return data
}

// point_status 변경 — del_place 별도 endpoint 사용
export async function updatePointStatus(pointId, cdId, status) {
  const { data } = await api.get('api/del_place', {
    params: { id: pointId, device_id: cdId, place_status: status },
  })
  return data
}

// M2 Monitoring Point 삭제
export async function deleteM2Point(pointId) {
  const { data } = await api.get('api/delete_ex', { params: { p_idx_ex: pointId } })
  return data
}

// 일별 디바이스 status 리스트 (상태 탭)
//   to: 'YYYY-MM-DD HH:mm:ss', limit: 응답 행 수
export async function fetchDeviceStatus(pointId, to, limit = 2) {
  const params = {}
  if (to)    params.to    = to
  if (limit) params.limit = limit
  const { data } = await api.get(`api/API21/m2_device_status/day/${pointId}`, { params })
  return data?.results?.[0] ?? data?.results ?? []
}

// monitoring/status_list — Data Check / Health Check 공용
//   type: 1 (장비 통신상태=Health Check) / 2 (Data Check 일반) / 'leak' (누수음 데이터)
//   start_dt/end_dt: 'YYYYMMDD' 또는 빈 값
export async function fetchM2StatusList({ type, deviceSn, startDt = '', endDt = '' } = {}) {
  const { data } = await api.get('api/monitoring/status_list', {
    params: { type, device_sn: deviceSn, start_dt: startDt, end_dt: endDt },
  })
  // 응답 모양 다양 — list[0] 또는 list 형태 모두 대응
  const list = data?.list
  if (Array.isArray(list)) {
    return Array.isArray(list[0]) ? list[0] : list
  }
  return Array.isArray(data?.results?.[0]) ? data.results[0] : (data?.results || [])
}

// 호환용 — 기존 컴포넌트가 fetchDataCheck / fetchHealthCheck 를 import 하는 경우 fallback
export async function fetchDataCheck(pointId, deviceSn, sDate, eDate) {
  return fetchM2StatusList({ type: 2, deviceSn: deviceSn || pointId, startDt: sDate, endDt: eDate })
}
export async function fetchHealthCheck(pointId, deviceSn, sDate, eDate) {
  return fetchM2StatusList({ type: 1, deviceSn: deviceSn || pointId, startDt: sDate, endDt: eDate })
}

// 간이수압계(pressure_reading) 지점 목록 — 원격 모니터링 지도 오버레이용 (Wiplat 관리자 전용)
//   GET api/API21/pressure_reading/list?customer_id={선택 고객사}&press_value={bar|psi|kgf}
//   press_value 는 서버가 값을 변환해 내려주는 단위. 지도 단위 세그먼트와 같은 값을 넘기되
//   서버가 모르는 'hydraulic' 은 호출부(MapPanel.pressValueParam)에서 bar 로 바꿔 보낸다.
//   응답 모양은 다른 API21 목록 관례를 따라 results[0] 우선, rows / 배열 직접도 허용
export async function fetchPressureReadingList({ customerId, pressValue = 'bar' } = {}, signal) {
  //const { data } = await api.get('api/API21/pressure_reading/list', {
  const { data } = await api.get('api/pressure_reading/list', {
    params: { customer_id: customerId, press_value: pressValue },
    signal,
  })
  if (Array.isArray(data?.results?.[0])) return data.results[0]
  if (Array.isArray(data?.results))      return data.results
  if (Array.isArray(data?.rows))         return data.rows
  return Array.isArray(data) ? data : []
}
